import { FC, useState } from "react";
import { Button, Pagination } from "react-bootstrap";
import { SpendAccount } from "@models/spendaccount.model";
import { Credit } from "@models/credit.model";
import { Debit } from "@models/debit.model";
import { formatCurrency } from "src/shared/utils/formatCurrency";

interface LedgerEntry {
  key: string;
  kind: "credit" | "debit";
  date: string | null;
  typeLabel: string;
  reporter: string | null;
  reason: string | null;
  amount: number; // signed: credits positive, debits negative
  credit?: Credit;
  debit?: Debit;
}

function buildLedger(credits: readonly Credit[], debits: readonly Debit[]): LedgerEntry[] {
  const entries: LedgerEntry[] = [
    ...credits.map((c) => ({
      key: `credit-${c.id}`,
      kind: "credit" as const,
      date: c.effective_date,
      typeLabel: c.source_type,
      reporter: null,
      reason: c.reason,
      amount: c.amount,
      credit: c,
    })),
    ...debits.map((d) => ({
      key: `debit-${d.id}`,
      kind: "debit" as const,
      date: d.effective_date,
      typeLabel: d.source_type,
      reporter: d.reporter,
      reason: d.reason,
      amount: -d.amount,
      debit: d,
    })),
  ];
  // Most recent first; entries without a date sort to the bottom.
  return entries.sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""));
}

const LEDGER_PAGE_SIZE = 8;

export interface SpendHandlers {
  onAddCredit?: () => void;
  onAddDebit?: () => void;
  onEditCredit?: (credit: Credit) => void;
  onDeleteCredit?: (credit: Credit) => void;
  onEditDebit?: (debit: Debit) => void;
  onDeleteDebit?: (debit: Debit) => void;
}

interface Props extends SpendHandlers {
  spendAccount: SpendAccount | null;
  credits: readonly Credit[];
  debits: readonly Debit[];
  /** Authoritative balance from the balance endpoint; falls back to the account total. */
  balance: number | null;
}

const ContractSpendPanel: FC<Props> = ({
  spendAccount,
  credits,
  debits,
  balance,
  onAddCredit,
  onAddDebit,
  onEditCredit,
  onDeleteCredit,
  onEditDebit,
  onDeleteDebit,
}) => {
  const editable = !!(onAddCredit || onAddDebit);
  const rowEditable = !!(onEditCredit || onDeleteCredit || onEditDebit || onDeleteDebit);
  const [page, setPage] = useState(0);

  if (!spendAccount) {
    return (
      <div className="text-center py-4 text-muted">
        <i className="bi bi-wallet2 fs-3 d-block mb-2 opacity-50" />
        <div className="small">No spend account for this contract yet</div>
      </div>
    );
  }

  const shownBalance = balance ?? spendAccount.balance;
  const ledger = buildLedger(credits, debits);

  const pageCount = Math.max(1, Math.ceil(ledger.length / LEDGER_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * LEDGER_PAGE_SIZE;
  const pageEntries = ledger.slice(start, start + LEDGER_PAGE_SIZE);

  return (
    <div className="spend-panel">
      <div className="spend-panel__head">
        <div>
          <div className="stat-label">Current balance</div>
          <div className={`spend-panel__balance ${shownBalance >= 0 ? "good" : "bad"}`}>
            {formatCurrency(shownBalance)}
          </div>
          <div className="spend-panel__account">
            {spendAccount.name || "Spend account"}
            <span className={`contract-status-badge ${spendAccount.is_active ? "active" : "inactive"} ms-2`}>
              {spendAccount.is_active ? "Active" : "Inactive"}
            </span>
          </div>
        </div>
        {editable && (
          <div className="d-flex gap-2">
            {onAddCredit && (
              <Button variant="outline-success" size="sm" onClick={onAddCredit}>
                <i className="bi bi-plus-circle me-1" />
                Credit
              </Button>
            )}
            {onAddDebit && (
              <Button variant="outline-danger" size="sm" onClick={onAddDebit}>
                <i className="bi bi-dash-circle me-1" />
                Debit
              </Button>
            )}
          </div>
        )}
      </div>

      {ledger.length === 0 ? (
        <div className="text-center py-3 text-muted small">No ledger entries yet</div>
      ) : (
        <table className="table table-sm spend-ledger mb-0">
          <thead>
            <tr>
              <th style={{ width: "16%" }}>Date</th>
              <th style={{ width: "18%" }}>Type</th>
              <th style={{ width: "18%" }}>Reporter</th>
              <th>Reason</th>
              <th className="text-end" style={{ width: "16%" }}>Amount</th>
              {rowEditable && <th style={{ width: "1%" }} />}
            </tr>
          </thead>
          <tbody>
            {pageEntries.map((e) => {
              const onEdit =
                e.kind === "credit"
                  ? onEditCredit && (() => onEditCredit(e.credit!))
                  : onEditDebit && (() => onEditDebit(e.debit!));
              const onDelete =
                e.kind === "credit"
                  ? onDeleteCredit && (() => onDeleteCredit(e.credit!))
                  : onDeleteDebit && (() => onDeleteDebit(e.debit!));
              return (
                <tr key={e.key}>
                  <td className="text-nowrap">{e.date ?? "—"}</td>
                  <td>
                    <span className={`contract-status-badge ${e.kind === "credit" ? "active" : "inactive"}`}>
                      {e.typeLabel}
                    </span>
                  </td>
                  <td className="text-muted">{e.reporter || "—"}</td>
                  <td className="text-muted">{e.reason || "—"}</td>
                  <td className={`text-end fw-semibold ${e.amount >= 0 ? "amount-pos" : "amount-neg"}`}>
                    {e.amount >= 0 ? "+" : "−"}
                    {formatCurrency(Math.abs(e.amount))}
                  </td>
                  {rowEditable && (
                    <td className="text-end text-nowrap">
                      {onEdit && (
                        <Button variant="link" size="sm" className="p-1 text-secondary" title="Edit" onClick={onEdit}>
                          <i className="bi bi-pencil" />
                        </Button>
                      )}
                      {onDelete && (
                        <Button variant="link" size="sm" className="p-1 text-danger" title="Delete" onClick={onDelete}>
                          <i className="bi bi-trash" />
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {ledger.length > LEDGER_PAGE_SIZE && (
        <div className="d-flex align-items-center justify-content-between mt-2 flex-wrap gap-2">
          <div className="text-muted small">
            Showing {start + 1}–{start + pageEntries.length} of {ledger.length}
          </div>
          <Pagination size="sm" className="mb-0">
            <Pagination.Prev
              disabled={safePage <= 0}
              onClick={() => setPage(safePage - 1)}
            />
            <Pagination.Item active>{`${safePage + 1} of ${pageCount}`}</Pagination.Item>
            <Pagination.Next
              disabled={safePage >= pageCount - 1}
              onClick={() => setPage(safePage + 1)}
            />
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default ContractSpendPanel;
